import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import cookieService from './cookieService';
import {
  routes,
  providerListEndpoint,
  PROVIDER_DETAILS_COOKIE,
  cpdTtl,
  envUrlClinicianManagment,
} from '../config';
import providerDetails from '../actions/providerDetailsAction';
import getHeaders from '../utils/getHeaders';
import store from '../store';
import createPatientAPIErrorModal from '../actions/createPatientAPIErrorAction';
import { logger } from '../logger';
import { regionMap } from '../data/regionMap';
import localStorageService from './localStorageService';

const log = logger();

const getProviderDetails = () => {
  const dispatch = useDispatch();

  // Get the providers details from state
  const pDetails = useSelector((state) => state.providerDetails);
  if (pDetails) return pDetails;

  // If not in state, get from cookie
  const cookieContent = cookieService.getCookie(
    PROVIDER_DETAILS_COOKIE,
  );
  if (cookieContent) {
    const providerDetailsData = cookieContent;

    // store in state
    dispatch(providerDetails(providerDetailsData));
    return providerDetailsData;
  }

  return null;
};

const getCachedProviders = () => {
  // get item from local storage with honourExpiry set to true
  // i.e the local storage item will be deleted if it's expired
  return localStorageService.getItem(PROVIDER_DETAILS_COOKIE, true);
};

const searchClinics = async (
  countryCode,
  tradingName,
  page = 0,
  limit = 20,
) => {
  try {
    store.dispatch(createPatientAPIErrorModal(false));
    const response = await axios.get(
      `${providerListEndpoint}/search?countryCode=${countryCode}&tradingName=${tradingName}&page=${page}&limit=${limit}`,
      getHeaders(),
    );

    return response.data;
  } catch (err) {
    log.error('Error searching clinics', err);
    store.dispatch(createPatientAPIErrorModal(true));
    return null;
  }
};

const getProviderListBackend = async (
  professionalId,
  professionalCountry,
  customHeaders = { 'cache-control': 'no-cache' },
) => {
  try {
    const response = await axios.get(
      `${providerListEndpoint}?professionalCountry=${professionalCountry}&professionalId=${professionalId}`,
      getHeaders(customHeaders),
    );
    return response.data;
  } catch (err) {
    log.error('Error getting provider list', err);
    return null;
  }
};

const getSearchResult = async (
  providerId,
  searchParams,
  page,
  pageSize,
) => {
  const requestBody = {
    query: {
      queryData: searchParams.queryData,
      countryCode: searchParams.countryCode,
      name: searchParams.name,
      dateOfBirth: searchParams.dateOfBirth,
      serialNumber: searchParams.deviceSerial,
    },
  };

  try {
    const response = await axios.post(
      `${providerListEndpoint}/${providerId}/patients/search?page=${page}&limit=${pageSize}`,
      requestBody,
      getHeaders(),
    );
    return response;
  } catch (err) {
    log.error('Error getting search result', err);
    return err.response;
  }
};

const locatePatient = async (params) => {
  const requestBody = params;

  try {
    const response = await axios.post(
      `${providerListEndpoint}/${params.relatedProfessionalProviderId}/patients/advanced-search`,
      requestBody,
      getHeaders(),
    );
    return response.data;
  } catch (err) {
    log.error('Error locating patient', err);
    return null;
  }
};

const checkClinics = async (
  professionalId,
  professionalCountry,
  useApiCache = false,
) => {
  let next = null;
  let hasClinic = true;
  let providerId = null;
  const customHeaders = useApiCache
    ? {}
    : { 'cache-control': 'no-cache' };

  const providerList = await getProviderListBackend(
    professionalId,
    professionalCountry,
    customHeaders,
  );
  if (!providerList) {
    next = routes.dpx.error;

    return {
      next,
      providerList,
      hasClinic,
      providerId,
    };
  }

  // check if country redirect flag is disabled
  const flagDisabled =
    providerList.providers[0]?.redirectFlag === 'DISABLED';

  if (!flagDisabled) {
    // filter clinics with no partyId (only if flag is on)
    const providersFiltered = providerList.providers.filter(
      (provider) => {
        return provider.partyId;
      },
    );

    providerList.providers = providersFiltered;
  }

  const providerCount = providerList.providers.length;

  switch (providerCount) {
    case 0:
      // no providers
      hasClinic = false;
      next = routes.dpx.initializer;
      break;

    case 1:
      // only one provider
      providerId = providerList.providers[0]?.id;
      next = routes.dpx.initializer;
      break;

    default:
      // multiple providers
      next = routes.dpx.selectClinics;
      break;
  }

  return {
    next,
    providerList,
    hasClinic,
    providerId,
  };
};

const getCurrentProvider = () => {
  const pDetails = getProviderDetails();
  const chosenProvider = pDetails?.providerId;
  if (!chosenProvider) return {};
  const currentProvider = pDetails.providerList.providers.filter(
    (provider) => provider.id === chosenProvider,
  )[0];
  return currentProvider || {};
};

const getCurrentPartyId = () => {
  return getCurrentProvider().partyId;
};

const getCurrentCId = () => {
  return getCurrentProvider().id;
};

const storeProviderDetails = (
  providerId,
  providerList,
  countryCode,
) => {
  const providerDetailsData = {
    hasProviderError: false,
    hasClinic: true,
    providerId,
    providerList,
    countryCode,
  };
  localStorageService.setItem(
    PROVIDER_DETAILS_COOKIE,
    providerDetailsData,
    cpdTtl,
  );
  return providerDetailsData;
};

const removeProviderDetails = () => {
  localStorageService.removeItem(PROVIDER_DETAILS_COOKIE);
  return true;
};

const updateProviderDetails = (key, value) => {
  localStorageService.updateItem(PROVIDER_DETAILS_COOKIE, {
    [key]: value,
  });

  return localStorageService.getItem(PROVIDER_DETAILS_COOKIE);
};

const getProviderRegion = (providerCountryCode) => {
  const region = regionMap.find(
    (r) => r.countryCode === providerCountryCode,
  );
  return region?.region;
};

const getCurrentProviderRegion = () => {
  // Tier 1: Try to get region from current provider object (backend data)
  const currentProvider = getCurrentProvider();
  if (currentProvider?.region) return currentProvider.region;

  // Tier 2: Try to get region from cached provider details in localStorage
  const cachedProviders = getCachedProviders();
  if (cachedProviders?.providerList?.providers) {
    const provider = cachedProviders.providerList.providers.find(
      (p) => p.id === cachedProviders.providerId,
    );
    if (provider?.region) return provider.region;
  }

  // Tier 3: Fallback to static region map using country code
  const countryCode =
    currentProvider?.countryCode || cachedProviders?.countryCode;
  if (countryCode) return getProviderRegion(countryCode);

  return undefined;
};

/**
 * Fetch the list of professionals for a given provider.
 * @param {string} providerId
 * @returns {Promise<Array>|null} List of professionals or null if error occurs.
 */
const getProfessionalList = async (providerId) => {
  const endpoint = `${envUrlClinicianManagment}/api/provider/professionals/${providerId}`;
  // call with no-cache header as this is meant to preload the cache
  const customHeaders = { 'cache-control': 'no-cache' };

  try {
    const response = await axios.get(
      endpoint,
      getHeaders(customHeaders),
    );
    return response.data;
  } catch (err) {
    log.error('Error getting professional list', err);
    return null;
  }
};

const providerService = {
  getProviderDetails,
  getProviderListBackend,
  getSearchResult,
  checkClinics,
  getCurrentPartyId,
  getCurrentCId,
  getCurrentProvider,
  locatePatient,
  searchClinics,
  storeProviderDetails,
  removeProviderDetails,
  updateProviderDetails,
  getProviderRegion,
  getCurrentProviderRegion,
  getCachedProviders,
  getProfessionalList,
};

export default providerService;
